import {
  useDocumentStore,
  useDocumentsTogether,
} from "@shared/components/providers/SupergrainProvider"
import type {
  CardStackAttributes,
  ParentCardStack,
  UnifiedCardStack,
} from "@shared/models/card-stack"
import type { CourseColor } from "@shared/models/colors"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import styles from "cc-frontend/components/planner/card-stacks/unit/header.module.css"
import { CHANGE_UNIT_COLOR } from "cc-frontend/lib/actions/course/calendar/CHANGE_UNIT_COLOR"
import copyLessonAction from "cc-frontend/lib/actions/lesson/LESSON_COPY"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Notifier from "cc-frontend/services/notifier"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type Session from "cc-frontend/services/session"
import { cloneDeep, isNil } from "lodash-es"
import { useMemo } from "react"
import { useAsEmberDocument } from "react-app/hooks/useAsEmberDocument"
import { useDispatch } from "react-app/hooks/useDispatch"
import { useOpenStandardsModal } from "react-app/hooks/useOpenStandardsModal"
import { useEmberService } from "react-app/providers/ember-service-context"
import type { UnitDeletionScope } from "./unified-unit/unit-menu-views/DeleteUnitView"
import UnitMenu from "./unified-unit/UnitMenu"
import UnitTitleBar from "./unified-unit/UnitTitleBar"
import UnifiedCardStackEditor from "./UnifiedCardStackEditor"

interface Props {
  cardStack: UnifiedCardStack & { attributes: { usedAs: "unit" } }
  planbook: Planbook
  rotationCalendar: RotationCalendar
  course: Course
  /**
   * The unit belongs to a course this view is only comparing against — a course this one
   * follows, say. It paints, but nothing here edits or comments on it.
   */
  readonly?: boolean
}

/**
 * A unit in the unit planner: course title, its menu, and the unit's cards. It is also
 * the single place the unit touches Ember services and the dispatcher — the pieces it
 * renders take data and the callbacks defined here, oblivious to what they tie into.
 */
export default tracked(function UnifiedUnit({
  cardStack,
  planbook,
  rotationCalendar,
  course,
  readonly = false,
}: Props) {
  /* -------------------------------------------------------------------------- */
  /* Ember services */
  /* -------------------------------------------------------------------------- */

  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const planbookEditor = useEmberService<PlanbookEditor>("planbook-editor")
  const modalManager = useEmberService<ModalManager>("modal-manager")
  const dispatcher = useEmberService<Dispatcher>("dispatcher")
  const notifier = useEmberService<Notifier>("notifier")
  const session = useEmberService<Session>("session")
  const asEmberDocument = useAsEmberDocument()
  const dispatch = useDispatch()
  const store = useDocumentStore()

  /* -------------------------------------------------------------------------- */
  /* This unit */
  /* -------------------------------------------------------------------------- */

  // The unit's date range is an inner property of the course, not a document of its own.
  const unitInfo = course.attributes.calendar.units.find((unit) => unit.id === cardStack.id)

  // `@findMany("unitInfo.ancestorIds", "card-stack-summary")`: the header's ancestor chips.
  const ancestorUnits =
    useDocumentsTogether("card-stack-summary", unitInfo?.ancestorIds ?? []).value ?? []

  const canEditUnit = (cardStack.meta?.permissions?.includes("can-edit-plan") ?? false) && !readonly
  const canPasteUnit = canEditUnit && !isNil(session.userId)

  // A unit isn't a lesson, so it only needs the trivial ModelsNeededForFirstEdit.
  const modelsNeededForFirstEdit: ModelsNeededForFirstEdit = useMemo(
    () => ({ cardStack }),
    [cardStack],
  )

  /* -------------------------------------------------------------------------- */
  /* Actions passed to the menu and the editor */
  /* -------------------------------------------------------------------------- */

  const openStandardsModal = useOpenStandardsModal(planbook, course)

  const copyUnit = () => {
    if (session.isAuthenticated !== true) {
      // "Copy Lesson" is the analytics label the legacy unit menu reported, kept so the
      // must-sign-in funnel stays comparable across the migration.
      session.mustSignInToProceed("Copy Lesson")
      return
    }
    if (isNil(session.userId)) return
    dispatch(
      copyLessonAction({
        lessonToCopy: cardStack,
        cardStackId: cardStack.id,
        currentUserId: session.userId,
        ownerIds: [planbook.relationships.owner.data.id],
        planbookId: planbook.id,
        courseId: course.id,
      }),
    )
    notifier.success("", {
      title: "Unit Copied!",
      message: "Click a unit's paste button to paste.",
      duration: 1000,
    })
  }

  const pasteUnit = () => {
    if (!canPasteUnit) return
    void (async () => {
      // Read the handle's CURRENT value: the find promise memoizes its first
      // resolution, so it can hand back a stale user from before the copy wrote
      // the clipboard.
      const handle = store.find("user", session.userId)
      const user = handle.value ?? (await handle.promise)
      if (isNil(user?.attributes?.clipboard?.cardStackAttributes)) {
        notifier.warning("You need to copy a unit first to paste it")
        return
      }
      const attributes: CardStackAttributes = cloneDeep(
        user.attributes.clipboard.cardStackAttributes,
      )
      const parentCardStack: ParentCardStack = attributes.parentCardStack!
      lessonEditor.pasteCardStack(
        modelsNeededForFirstEdit,
        cardStack.id,
        course,
        planbook,
        attributes,
        cardStack.attributes,
        "unit",
        parentCardStack,
      )
    })()
  }

  const eraseUnit = (scope: UnitDeletionScope) => {
    lessonEditor.eraseUnit(
      cardStack.id,
      course,
      planbook,
      rotationCalendar,
      scope === "unit-and-lessons",
    )
    // The unit is gone; anything that was showing it has nothing left to show.
    modalManager.closeModal("planner/course/timeline/sidebar")
    modalManager.closeModal("planner/modals/unit-comparison-modal")
  }

  const printOrDownload = () => {
    // The unit lost its calendar entry — it was just erased, and this island is still up.
    if (!unitInfo) return
    modalManager.openModal("planner/modals/print-download", {
      courseId: course.id,
      planbookId: planbook.id,
      unitId: cardStack.id,
      startDate: unitInfo.startDate,
      endDate: unitInfo.endDate,
      layout: "course-unit",
      canPrint: true,
    })
  }

  const browseUnitHistory = () => {
    modalManager.openModal("planner/modals/lesson-history", {
      cardStack: asEmberDocument(cardStack),
    })
  }

  // The menu calls this and closes, so nothing downstream is left to await the read. A template
  // that cannot be read has to say so here or the press does nothing at all.
  const pasteTemplate = (templateId: string) => {
    void pasteTemplateOrWarn(templateId).catch(() =>
      notifier.warning("We couldn't open that template. Please try again."),
    )
  }

  const pasteTemplateOrWarn = async (templateId: string) => {
    // Prefer the handle's current value: the find promise memoizes its first resolution.
    const templateHandle = store.find("card-stack", templateId)
    const template = templateHandle.value ?? (await templateHandle.promise!)
    lessonEditor.pasteCardStack(
      modelsNeededForFirstEdit,
      cardStack.id,
      course,
      planbook,
      template.attributes,
      cardStack.attributes,
      "template",
      {
        id: template.id,
        version: template.attributes.version,
        // The template's own planbook, which for a shared template is the group's — not this
        // planbook, which never held it.
        planbookId: template.relationships.planbook.data.id,
        courseId: null,
        ownerIds: [planbook.relationships.owner.data.id],
        source: "template",
      },
    )
  }

  const openTemplateGallery = () => {
    modalManager.openModal("planner/modals/template-library", {
      cardStack: asEmberDocument(cardStack),
      course: asEmberDocument(course),
      planbook: asEmberDocument(planbook),
      rotationCalendar: asEmberDocument(rotationCalendar),
      // These are just for actions, not Ember rendering -> no need to convert.
      modelsNeededForFirstEdit,
    })
  }

  const changeUnitColor = (color: CourseColor | null) => {
    dispatcher.dispatchAction(CHANGE_UNIT_COLOR, {
      courseId: course.id,
      cardStackId: cardStack.id,
      color,
      previousColor: cardStack.attributes.color,
    })
  }

  const saveAsTemplate = (name: string) => {
    planbookEditor.saveAsLessonTemplate(course, planbook, cardStack.attributes, name)
    // Saving from the unit-comparison modal is the end of that comparison.
    dispatcher.dispatch("CLOSE_UNIT_COMPARISON_MODAL", {
      sessionId: session.id,
      componentId: "unit-comparison-modal",
    })
  }

  return (
    // Ember's unit: the course's color shows only as the strip pt-2.25 leaves above the white
    // body, not as a filled bar.
    <div
      className="take-course-background-color set-course-color-vars mb-3 rounded-md pt-2.25 shadow-md"
      data-color={course.attributes.color}
    >
      <div className="rounded-b-md bg-white p-3">
        {ancestorUnits.length > 0 && (
          <div className="mb-1.5 flex flex-row flex-wrap">
            {ancestorUnits.map((ancestor) => (
              <div
                className={`take-course-background-color set-course-color-vars ${styles["units-block__unit"]}`}
                data-color={ancestor.attributes.color}
                key={ancestor.id}
              >
                {ancestor.attributes.title}
              </div>
            ))}
          </div>
        )}

        <UnitTitleBar
          course={course}
          menu={
            <UnitMenu
              unit={cardStack}
              course={course}
              planbook={planbook}
              copyUnit={copyUnit}
              pasteUnit={pasteUnit}
              canPasteUnit={canPasteUnit}
              eraseUnit={eraseUnit}
              printOrDownload={printOrDownload}
              browseUnitHistory={browseUnitHistory}
              openTemplateGallery={openTemplateGallery}
              pasteTemplate={pasteTemplate}
              saveAsTemplate={saveAsTemplate}
              changeUnitColor={changeUnitColor}
              readonly={readonly}
            />
          }
        />

        <UnifiedCardStackEditor
          cardStack={cardStack}
          modelsNeededForFirstEdit={modelsNeededForFirstEdit}
          isNonInstructional={false}
          openStandardsModal={openStandardsModal}
          readonly={readonly}
        />
      </div>
    </div>
  )
})
